import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from './axiosSetup';
import API_BASE from './apiBase';
import Swal from 'sweetalert2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faSave,
  faPlus,
  faTrash,
  faUndo,
  faEye,
  faDesktop,
  faTabletAlt,
  faMobileAlt,
  faFont,
  faSlidersH,
  faClone,
  faSearch,
  faThLarge,
  faTimes,
  faEdit,
  faFileAlt,
  faCheckCircle,
  faCalendarAlt,
  faLayerGroup
} from '@fortawesome/free-solid-svg-icons';

const DEFAULT_STYLE_CONFIG = {
  fontFamily: "'Inter', sans-serif",
  fontSize: '15px',
  textAlign: 'left',
  bold: false,
  italic: false,
  underline: false,
};

const DEFAULT_TEMPLATE_DATA = {
  template_name: 'Sample Dummy Consent Template',
  consent_title: 'Data Privacy & Consent Notice',
  description: 'Sample consent statement and terms preview for statutory privacy compliance.',
  consent_text:
    'We value your privacy. By proceeding, you acknowledge that your personal information will be collected, stored, and processed in accordance with our Privacy Policy and relevant data protection regulations. You can withdraw your consent at any time.',
  style_config: DEFAULT_STYLE_CONFIG,
};

export default function DummyConsentPage({ token, user, onBack }) {
  // ── Template State ──
  const [templateList, setTemplateList] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null); // null = new
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // View mode: 'cards' (All Template Cards Grid) vs 'builder' (Interactive Studio)
  const [viewMode, setViewMode] = useState('cards');
  const [searchQuery, setSearchQuery] = useState('');

  // Quick preview modal state
  const [previewModalTemplate, setPreviewModalTemplate] = useState(null);
  const [modalDevice, setModalDevice] = useState('desktop');

  // Form Fields (Builder state)
  const [templateName, setTemplateName] = useState(DEFAULT_TEMPLATE_DATA.template_name);
  const [consentTitle, setConsentTitle] = useState(DEFAULT_TEMPLATE_DATA.consent_title);
  const [description, setDescription] = useState(DEFAULT_TEMPLATE_DATA.description);
  const [consentText, setConsentText] = useState(DEFAULT_TEMPLATE_DATA.consent_text);
  const [styleConfig, setStyleConfig] = useState(DEFAULT_STYLE_CONFIG);

  // Last saved checkpoint for "Reset" action
  const [lastSavedCheckpoint, setLastSavedCheckpoint] = useState(DEFAULT_TEMPLATE_DATA);

  // Preview viewport device switcher inside Builder
  const [previewDevice, setPreviewDevice] = useState('desktop'); // desktop, tablet, mobile

  // Auth Header
  const authHeaders = useMemo(() => {
    const tk = token || localStorage.getItem('token') || '';
    return tk ? { Authorization: `Bearer ${tk}` } : {};
  }, [token]);

  // Robust candidate URL builder
  const getCandidateUrls = (path) => {
    const cleanPath = path.startsWith('/') ? path : '/' + path;
    const base = (API_BASE || (typeof window !== 'undefined' && window.__API_BASE__) || '').toString().trim().replace(/\/+$/, '');
    const set = new Set();
    if (base) {
      if (base.endsWith('/api')) {
        set.add(base + cleanPath);
        set.add(base.replace(/\/api$/, '') + cleanPath);
      } else {
        set.add(base + cleanPath);
        set.add(base + '/api' + cleanPath);
      }
    }
    set.add(cleanPath);
    set.add('/api' + cleanPath);
    return Array.from(set);
  };

  const requestApi = async (method, path, data = null) => {
    const urls = getCandidateUrls(path);
    let lastErr = null;
    const tk = token || localStorage.getItem('token') || '';
    const headers = { 'Content-Type': 'application/json', 'Accept': 'application/json' };
    if (tk) headers['Authorization'] = `Bearer ${tk}`;

    for (const url of urls) {
      try {
        const res = await axios({
          method,
          url,
          data: data ? data : undefined,
          headers,
          skipLoading: true
        });
        if (res && res.data) return res.data;
      } catch (err) {
        lastErr = err;
        const st = err.response ? err.response.status : null;
        if (st === 404 || st === 405) continue;
        throw err;
      }
    }
    throw lastErr || new Error('Request failed on all endpoint candidates');
  };

  // ── Fetch Saved Templates ──
  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const res = await requestApi('get', '/admin/dummy-consent');
      const list = Array.isArray(res) ? res : (res && res.data && Array.isArray(res.data)) ? res.data : [];
      setTemplateList(list);
    } catch (err) {
      console.error('Failed to load dummy consent templates:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  // ── Load a Specific Template into Builder ──
  const loadTemplate = (tmpl, switchToBuilder = false) => {
    if (!tmpl) return;
    setSelectedTemplateId(tmpl.id);
    setTemplateName(tmpl.template_name || '');
    setConsentTitle(tmpl.consent_title || '');
    setDescription(tmpl.description || '');
    setConsentText(tmpl.consent_text || '');

    const st = tmpl.style_config && typeof tmpl.style_config === 'object'
      ? { ...DEFAULT_STYLE_CONFIG, ...tmpl.style_config }
      : DEFAULT_STYLE_CONFIG;
    setStyleConfig(st);

    setLastSavedCheckpoint({
      template_name: tmpl.template_name || '',
      consent_title: tmpl.consent_title || '',
      description: tmpl.description || '',
      consent_text: tmpl.consent_text || '',
      style_config: st,
    });

    if (switchToBuilder) {
      setViewMode('builder');
    }
  };

  // ── Handle "New Template" ──
  const handleNewTemplate = () => {
    setSelectedTemplateId(null);
    setTemplateName('New Sample Consent Template');
    setConsentTitle('Data Privacy & Consent Notice');
    setDescription('Sample consent template for design and preview.');
    setConsentText(DEFAULT_TEMPLATE_DATA.consent_text);
    setStyleConfig(DEFAULT_STYLE_CONFIG);
    setLastSavedCheckpoint(DEFAULT_TEMPLATE_DATA);
    setViewMode('builder');
  };

  // ── Handle "Reset" ──
  const handleReset = () => {
    Swal.fire({
      title: 'Reset Unsaved Changes?',
      text: 'This will revert all fields and styles to the last saved checkpoint.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#2563eb',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Yes, Reset',
    }).then((res) => {
      if (res.isConfirmed) {
        setTemplateName(lastSavedCheckpoint.template_name);
        setConsentTitle(lastSavedCheckpoint.consent_title);
        setDescription(lastSavedCheckpoint.description);
        setConsentText(lastSavedCheckpoint.consent_text);
        setStyleConfig(lastSavedCheckpoint.style_config);
        Swal.fire({
          title: 'Reset Completed',
          text: 'Fields have been restored to the last saved version.',
          icon: 'success',
          timer: 1500,
          showConfirmButton: false,
        });
      }
    });
  };

  // ── Handle "Save" (Create or Update) ──
  const handleSave = async (saveAsNew = false) => {
    if (!templateName.trim()) {
      Swal.fire({
        title: 'Validation Error',
        text: 'Template name is required.',
        icon: 'warning',
      });
      return;
    }

    setSaving(true);
    const payload = {
      template_name: templateName.trim(),
      consent_title: consentTitle.trim(),
      description: description.trim(),
      consent_text: consentText,
      consent_options: null,
      style_config: styleConfig,
      tenant_id: user?.tenant_id || null,
    };

    try {
      let res;
      if (selectedTemplateId && !saveAsNew) {
        res = await requestApi('put', `/admin/dummy-consent/${selectedTemplateId}`, payload);
        Swal.fire({
          title: 'Saved Successfully',
          text: `Template "${templateName}" updated successfully in dummy_consent table.`,
          icon: 'success',
          timer: 2000,
          showConfirmButton: false,
        });
      } else {
        res = await requestApi('post', '/admin/dummy-consent', payload);
        const createdId = res?.data?.id || res?.id;
        if (createdId) setSelectedTemplateId(createdId);
        Swal.fire({
          title: 'Created Successfully',
          text: `Dummy template "${templateName}" saved to dummy_consent table.`,
          icon: 'success',
          timer: 2000,
          showConfirmButton: false,
        });
      }

      setLastSavedCheckpoint({
        template_name: templateName,
        consent_title: consentTitle,
        description: description,
        consent_text: consentText,
        style_config: styleConfig,
      });

      await fetchTemplates();
    } catch (err) {
      console.error('Save failed:', err);
      const detail = err.response?.data?.detail || err.message || 'Error saving template';
      Swal.fire({
        title: 'Save Failed',
        text: detail,
        icon: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  // ── Handle "Duplicate / Clone" ──
  const handleDuplicate = async (tmpl) => {
    if (!tmpl) return;
    try {
      const payload = {
        template_name: `${tmpl.template_name || 'Template'} (Copy)`,
        consent_title: tmpl.consent_title || '',
        description: tmpl.description || '',
        consent_text: tmpl.consent_text || '',
        consent_options: tmpl.consent_options || null,
        style_config: tmpl.style_config || DEFAULT_STYLE_CONFIG,
        tenant_id: user?.tenant_id || tmpl.tenant_id || null,
      };
      const res = await requestApi('post', '/admin/dummy-consent', payload);
      Swal.fire({
        title: 'Duplicated!',
        text: `Created copy "${payload.template_name}"`,
        icon: 'success',
        timer: 1800,
        showConfirmButton: false,
      });
      await fetchTemplates();
      const created = res?.data || res;
      if (created) {
        loadTemplate(created, true);
      }
    } catch (err) {
      Swal.fire({ title: 'Error', text: 'Failed to duplicate template', icon: 'error' });
    }
  };

  // ── Handle "Delete" ──
  const handleDelete = (tmplId = null, tmplName = null) => {
    const idToDelete = tmplId || selectedTemplateId;
    const nameToDelete = tmplName || templateName;
    if (!idToDelete) return;

    Swal.fire({
      title: 'Delete Dummy Template?',
      text: `Are you sure you want to delete template "${nameToDelete}"? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Yes, Delete',
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await requestApi('delete', `/admin/dummy-consent/${idToDelete}`);
          Swal.fire({
            title: 'Deleted',
            text: 'Dummy template has been removed from dummy_consent table.',
            icon: 'success',
            timer: 1800,
            showConfirmButton: false,
          });
          if (idToDelete === selectedTemplateId) {
            handleNewTemplate();
            setViewMode('cards');
          }
          await fetchTemplates();
        } catch (err) {
          const detail = err.response?.data?.detail || 'Failed to delete template';
          Swal.fire({ title: 'Error', text: detail, icon: 'error' });
        }
      }
    });
  };

  // ── Style & Formatting Updaters ──
  const updateStyle = (key, value) => {
    setStyleConfig((prev) => ({ ...prev, [key]: value }));
  };

  // ── Device width mapping for preview ──
  const getDeviceWidthStyle = (device) => {
    switch (device) {
      case 'mobile':
        return { maxWidth: '375px', width: '100%' };
      case 'tablet':
        return { maxWidth: '600px', width: '100%' };
      default:
        return { maxWidth: '640px', width: '100%' };
    }
  };

  const previewWidthStyle = useMemo(() => getDeviceWidthStyle(previewDevice), [previewDevice]);

  // Format date helper
  const formatDate = (isoStr) => {
    if (!isoStr) return '';
    try {
      const d = new Date(isoStr);
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch (e) {
      return '';
    }
  };

  // Filtered templates list for search
  const filteredTemplates = useMemo(() => {
    if (!searchQuery.trim()) return templateList;
    const q = searchQuery.toLowerCase();
    return templateList.filter((t) => {
      return (
        (t.template_name || '').toLowerCase().includes(q) ||
        (t.consent_title || '').toLowerCase().includes(q) ||
        (t.description || '').toLowerCase().includes(q) ||
        (t.consent_text || '').toLowerCase().includes(q) ||
        String(t.id).includes(q)
      );
    });
  }, [templateList, searchQuery]);

  return (
    <div className="dummy-consent-container">
      <style>{`
        .dummy-consent-container { min-height: calc(100vh - 70px); background: #f8fafc; padding: 1.25rem 2rem 2.5rem; box-sizing: border-box; font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; color: #1e293b; }
        .dummy-header-bar { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 1rem; margin-bottom: 1.25rem; background: #ffffff; padding: 1.1rem 1.6rem; border-radius: 12px; border: 1.5px solid #e2e8f0; box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04); }
        .dummy-header-left { display: flex; align-items: center; gap: 1rem; }
        .dummy-title-wrap h1 { font-size: 1.45rem; font-weight: 800; margin: 0; color: #0f172a; letter-spacing: -0.02em; }
        .dummy-subtitle { font-size: 0.84rem; color: #64748b; margin: 0.2rem 0 0; }
        .dummy-header-actions { display: flex; align-items: center; gap: 0.6rem; flex-wrap: wrap; }
        .dummy-btn { display: inline-flex; align-items: center; gap: 0.45rem; font-size: 0.85rem; font-weight: 600; padding: 0.55rem 1rem; border-radius: 8px; border: 1px solid transparent; cursor: pointer; transition: all 0.15s ease-in-out; text-decoration: none; }
        .dummy-btn-primary { background: #2563eb; color: #ffffff; }
        .dummy-btn-primary:hover { background: #1d4ed8; box-shadow: 0 2px 6px rgba(37, 99, 235, 0.35); }
        .dummy-btn-secondary { background: #ffffff; color: #334155; border-color: #cbd5e1; }
        .dummy-btn-secondary:hover { background: #f1f5f9; border-color: #94a3b8; }
        .dummy-btn-danger { background: #ffffff; color: #dc2626; border-color: #fecaca; }
        .dummy-btn-danger:hover { background: #fef2f2; border-color: #f87171; }
        .dummy-btn-outline { background: transparent; color: #475569; border-color: #cbd5e1; }
        .dummy-btn-outline:hover { background: #f8fafc; color: #1e293b; }
        
        /* Multi-Template Quick Bar */
        .dummy-quick-bar { background: #ffffff; border: 1.5px solid #e2e8f0; border-radius: 12px; padding: 12px 18px; margin-bottom: 1.25rem; display: flex; flex-direction: column; gap: 10px; box-shadow: 0 1px 3px rgba(0,0,0,0.03); }
        .dummy-quick-row { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 10px; }
        .dummy-chips-wrap { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; padding-top: 8px; border-top: 1px solid #f1f5f9; }
        .dummy-chip-btn { background: #f8fafc; color: #334155; border: 1px solid #cbd5e1; border-radius: 20px; padding: 4px 12px; font-size: 0.78rem; font-weight: 500; cursor: pointer; display: flex; align-items: center; gap: 6px; transition: all 0.15s ease; }
        .dummy-chip-btn:hover { background: #e2e8f0; }
        .dummy-chip-btn.active { background: #2563eb; color: #ffffff; border-color: #2563eb; font-weight: 700; box-shadow: 0 2px 5px rgba(37,99,235,0.25); }

        /* KPI Cards Grid */
        .dummy-kpi-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 1rem; margin-bottom: 1.25rem; }
        .dummy-kpi-card { background: #ffffff; border: 1.5px solid #e2e8f0; border-radius: 12px; padding: 16px 20px; box-shadow: 0 1px 3px rgba(0,0,0,0.03); }
        .dummy-kpi-label { font-size: 0.78rem; color: #64748b; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; }
        .dummy-kpi-value { font-size: 1.75rem; font-weight: 800; color: #0f172a; margin-top: 4px; }
        .dummy-kpi-sub { font-size: 0.76rem; color: #10b981; font-weight: 600; margin-top: 4px; }

        /* Template Cards Grid */
        .dummy-cards-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(360px, 1fr)); gap: 1.25rem; }
        @media (max-width: 640px) { .dummy-cards-grid { grid-template-columns: 1fr; } }
        .dummy-template-card { background: #ffffff; border: 1.5px solid #e2e8f0; border-radius: 14px; box-shadow: 0 2px 8px rgba(0,0,0,0.04); overflow: hidden; display: flex; flex-direction: column; justify-content: space-between; transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease; }
        .dummy-template-card:hover { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(0,0,0,0.06); border-color: #93c5fd; }
        .dummy-card-top { padding: 16px 18px 12px; border-bottom: 1px solid #f1f5f9; display: flex; align-items: flex-start; justify-content: space-between; gap: 10px; }
        .dummy-card-body-section { padding: 16px 18px; flex: 1; display: flex; flex-direction: column; gap: 12px; }
        .dummy-card-snippet-box { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 12px 14px; max-height: 120px; overflow: hidden; position: relative; }
        .dummy-card-badges { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
        .dummy-card-badge { font-size: 0.72rem; padding: 3px 8px; border-radius: 6px; font-weight: 600; background: #f1f5f9; color: #475569; border: 1px solid #e2e8f0; }
        .dummy-card-actions { padding: 12px 18px; background: #fafafa; border-top: 1px solid #e2e8f0; display: flex; align-items: center; justify-content: space-between; gap: 8px; flex-wrap: wrap; }

        /* Builder Layout */
        .dummy-main-grid { display: grid; grid-template-columns: minmax(420px, 1.15fr) minmax(460px, 1.25fr); gap: 1.5rem; align-items: start; }
        @media (max-width: 1100px) { .dummy-main-grid { grid-template-columns: 1fr; } }
        .dummy-editor-card { background: #ffffff; border: 1.5px solid #e2e8f0; border-radius: 12px; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04); overflow: hidden; }
        .dummy-card-header { padding: 1rem 1.25rem; border-bottom: 1.5px solid #e2e8f0; display: flex; align-items: center; justify-content: space-between; background: #fafafa; }
        .dummy-card-title { font-size: 1rem; font-weight: 700; color: #1e293b; margin: 0; display: flex; align-items: center; gap: 0.5rem; }
        .dummy-editor-body { padding: 1.25rem; display: flex; flex-direction: column; gap: 1.25rem; max-height: calc(100vh - 240px); overflow-y: auto; }
        .dummy-section { border: 1.5px solid #e2e8f0; border-radius: 9px; padding: 1.1rem; background: #ffffff; }
        .dummy-section-title { font-weight: 700; margin: 0 0 0.85rem 0; display: flex; align-items: center; justify-content: space-between; text-transform: uppercase; letter-spacing: 0.03em; font-size: 0.78rem; color: #64748b; }
        .dummy-form-group { margin-bottom: 0.9rem; }
        .dummy-form-group:last-child { margin-bottom: 0; }
        .dummy-label { display: block; font-size: 0.8rem; font-weight: 600; color: #334155; margin-bottom: 0.35rem; }
        .dummy-input, .dummy-textarea, .dummy-select { width: 100%; padding: 0.55rem 0.75rem; font-size: 0.875rem; border: 1.5px solid #cbd5e1; border-radius: 7px; background: #ffffff; color: #1e293b; box-sizing: border-box; font-family: inherit; transition: border-color 0.15s ease, box-shadow 0.15s ease; }
        .dummy-input:focus, .dummy-textarea:focus, .dummy-select:focus { outline: none; border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12); }
        .dummy-textarea { min-height: 140px; resize: vertical; line-height: 1.6; }
        .dummy-toolbar { display: flex; align-items: center; gap: 0.4rem; flex-wrap: wrap; padding: 0.45rem; background: #f1f5f9; border-radius: 7px; margin-bottom: 0.6rem; border: 1px solid #e2e8f0; }
        .dummy-tool-btn { background: #ffffff; border: 1px solid #cbd5e1; border-radius: 5px; padding: 0.35rem 0.6rem; font-size: 0.8rem; font-weight: 600; cursor: pointer; color: #334155; transition: all 0.12s ease; }
        .dummy-tool-btn:hover { background: #e2e8f0; }
        .dummy-tool-btn.active { background: #2563eb; color: #ffffff; border-color: #2563eb; }
        .dummy-tool-select { padding: 0.3rem 0.5rem; font-size: 0.8rem; border: 1px solid #cbd5e1; border-radius: 5px; background: #ffffff; color: #334155; }
        .dummy-preview-column { position: sticky; top: 1.5rem; }
        .dummy-preview-card { background: #ffffff; border: 1.5px solid #e2e8f0; border-radius: 12px; box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05); overflow: hidden; }
        .dummy-preview-header { padding: 0.85rem 1.25rem; background: #0f172a; color: #ffffff; display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 0.6rem; }
        .dummy-device-switcher { display: flex; background: #1e293b; border-radius: 6px; padding: 2px; gap: 2px; }
        .dummy-device-btn { background: transparent; border: none; color: #94a3b8; padding: 0.35rem 0.65rem; font-size: 0.75rem; border-radius: 4px; cursor: pointer; display: flex; align-items: center; gap: 0.35rem; font-weight: 500; transition: all 0.12s; }
        .dummy-device-btn:hover { color: #ffffff; }
        .dummy-device-btn.active { background: #3b82f6; color: #ffffff; }
        .dummy-preview-stage { background: #e2e8f0; background-image: radial-gradient(#cbd5e1 1px, transparent 1px); background-size: 16px 16px; padding: 2.5rem 1.5rem; min-height: 480px; display: flex; align-items: center; justify-content: center; overflow-x: auto; }
        .dummy-rendered-dialog { transition: all 0.2s ease-in-out; box-sizing: border-box; position: relative; overflow: hidden; background: #ffffff; border: 1px solid #e2e8f0; border-radius: 12px; box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1); }
        .dummy-preview-watermark { background: rgba(239, 68, 68, 0.08); border-bottom: 1px dashed rgba(239, 68, 68, 0.3); color: #dc2626; font-size: 0.72rem; font-weight: 700; text-align: center; padding: 0.4rem 0.75rem; letter-spacing: 0.06em; text-transform: uppercase; }
        .dummy-templates-select { font-size: 0.88rem; padding: 0.5rem 0.8rem; border: 1.5px solid #cbd5e1; border-radius: 7px; background: #ffffff; font-weight: 600; color: #1e293b; min-width: 220px; }
      `}</style>

      {/* ── Top Header Bar ── */}
      <div className="dummy-header-bar" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', flexWrap: 'wrap', gap: '12px' }}>
        <div className="dummy-header-left">
          <div className="dummy-title-wrap">
            <h1 style={{ margin: 0 }}>Dummy Consent Templates</h1>
            {/* <p className="dummy-subtitle" style={{ margin: '3px 0 0 0', fontSize: '0.85rem', color: '#64748b' }}>
              Sample Statutory & Design Templates ({templateList.length} Total Saved) • Safe Isolated Sandbox
            </p> */}
          </div>
        </div>

        {/* Header Actions: Create New Template -> View Switcher -> Back */}
        <div className="dummy-header-actions" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <button
            type="button"
            className="dummy-btn"
            style={{
              background: '#059669',
              color: '#ffffff',
              border: 'none',
              padding: '7px 15px',
              borderRadius: '8px',
              fontWeight: 700,
              fontSize: '0.86rem',
              cursor: 'pointer',
              boxShadow: '0 2px 5px rgba(5,150,105,0.25)',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.15s ease'
            }}
            onClick={handleNewTemplate}
            title="Create a new dummy consent template"
          >
            <FontAwesomeIcon icon={faPlus} />
            <span>Create New Template</span>
          </button>

          <div style={{
            display: 'inline-flex',
            background: '#f1f5f9',
            padding: '3px',
            borderRadius: '9px',
            border: '1px solid #cbd5e1'
          }}>
            <button
              type="button"
              onClick={() => setViewMode('cards')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '7px',
                padding: '7px 14px',
                borderRadius: '7px',
                border: 'none',
                fontSize: '0.86rem',
                fontWeight: 700,
                cursor: 'pointer',
                background: viewMode === 'cards' ? '#2563eb' : 'transparent',
                color: viewMode === 'cards' ? '#ffffff' : '#475569',
                boxShadow: viewMode === 'cards' ? '0 1px 3px rgba(37,99,235,0.3)' : 'none',
                transition: 'all 0.15s'
              }}
            >
              <FontAwesomeIcon icon={faThLarge} />
              <span>All Template Cards ({templateList.length})</span>
            </button>
            <button
              type="button"
              onClick={() => setViewMode('builder')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '7px',
                padding: '7px 14px',
                borderRadius: '7px',
                border: 'none',
                fontSize: '0.86rem',
                fontWeight: 700,
                cursor: 'pointer',
                background: viewMode === 'builder' ? '#2563eb' : 'transparent',
                color: viewMode === 'builder' ? '#ffffff' : '#475569',
                boxShadow: viewMode === 'builder' ? '0 1px 3px rgba(37,99,235,0.3)' : 'none',
                transition: 'all 0.15s'
              }}
            >
              <FontAwesomeIcon icon={faSlidersH} />
              <span>Template Builder</span>
            </button>
          </div>

          <button
            type="button"
            className="back-btn"
            onClick={() => {
              if (typeof onBack === 'function') onBack();
              else window.history.back();
            }}
            style={{
              padding: '7px 16px',
              borderRadius: '8px',
              border: '1.5px solid #cbd5e1',
              background: '#ffffff',
              color: '#334155',
              fontWeight: 700,
              fontSize: '0.86rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <FontAwesomeIcon icon={faArrowLeft} />
            <span>Back</span>
          </button>
        </div>
      </div>

      {/* ═════════════════════════════════════════════════════════════════════
         VIEW 1: ALL TEMPLATE CARDS GALLERY (`viewMode === 'cards'`)
         ═════════════════════════════════════════════════════════════════════ */}
      {viewMode === 'cards' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          {/* Search Bar */}
          <div style={{
            background: '#ffffff',
            border: '1.5px solid #e2e8f0',
            borderRadius: 12,
            padding: '16px 20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '12px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.03)'
          }}>
            <div style={{ position: 'relative', flex: 1, minWidth: '280px', maxWidth: '440px' }}>
              <FontAwesomeIcon icon={faSearch} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: '0.85rem' }} />
              <input
                type="text"
                placeholder="Search templates by name, title, description, statement..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px 8px 34px',
                  borderRadius: 8,
                  border: '1.5px solid #cbd5e1',
                  fontSize: '0.88rem',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ fontSize: '0.85rem', color: '#64748b' }}>
              Showing <b>{filteredTemplates.length}</b> of <b>{templateList.length}</b> templates
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  style={{ marginLeft: 10, background: '#f1f5f9', border: '1px solid #cbd5e1', padding: '3px 8px', borderRadius: 5, fontSize: '0.78rem', cursor: 'pointer' }}
                >
                  Clear Search
                </button>
              )}
            </div>
          </div>

          {/* Templates Cards Grid */}
          <div className="dummy-cards-grid">
            {filteredTemplates.map((tmpl) => {
              const isSelected = selectedTemplateId === tmpl.id;
              const st = tmpl.style_config && typeof tmpl.style_config === 'object' ? tmpl.style_config : DEFAULT_STYLE_CONFIG;

              return (
                <div
                  key={tmpl.id}
                  className="dummy-template-card"
                  style={{
                    borderColor: isSelected ? '#2563eb' : '#e2e8f0',
                    boxShadow: isSelected ? '0 0 0 2px rgba(37,99,235,0.2), 0 4px 12px rgba(0,0,0,0.05)' : undefined
                  }}
                >
                  {/* Card Top / Header */}
                  <div className="dummy-card-top">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{
                        width: 34,
                        height: 34,
                        borderRadius: 8,
                        background: '#eff6ff',
                        color: '#2563eb',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.95rem'
                      }}>
                        <FontAwesomeIcon icon={faFileAlt} />
                      </div>
                      <div>
                        <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#0f172a' }}>
                          {tmpl.template_name}
                        </h3>
                        {isSelected && (
                          <span style={{ fontSize: '0.72rem', color: '#2563eb', fontWeight: 700 }}>
                            ● Active in Builder
                          </span>
                        )}
                      </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span className="dummy-card-badge" style={{ background: '#f1f5f9', color: '#334155', fontWeight: 700 }}>
                        #{tmpl.id}
                      </span>
                      <span className="dummy-card-badge" style={{ background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca' }}>
                        Sample
                      </span>
                    </div>
                  </div>

                  {/* Card Content & Preview Snippet */}
                  <div className="dummy-card-body-section">
                    <div>
                      <div style={{ fontSize: '0.76rem', color: '#64748b', fontWeight: 700, textTransform: 'uppercase', marginBottom: 3 }}>
                        Consent Title
                      </div>
                      <div style={{ fontSize: '0.94rem', fontWeight: 700, color: '#1e293b' }}>
                        {tmpl.consent_title || 'Data Privacy Notice'}
                      </div>
                    </div>

                    {tmpl.description && (
                      <p style={{ margin: 0, fontSize: '0.8rem', color: '#64748b', fontStyle: 'italic', lineHeight: 1.4 }}>
                        {tmpl.description}
                      </p>
                    )}

                    {/* Styled Visual Statement Preview Box */}
                    <div>
                      <div style={{ fontSize: '0.74rem', color: '#64748b', fontWeight: 600, marginBottom: 4 }}>
                        Statement Preview (Rendered Style):
                      </div>
                      <div
                        className="dummy-card-snippet-box"
                        style={{
                          fontFamily: st.fontFamily || "'Inter', sans-serif",
                          fontSize: st.fontSize || '14px',
                          textAlign: st.textAlign || 'left',
                          fontWeight: st.bold ? '700' : '400',
                          fontStyle: st.italic ? 'italic' : 'normal',
                          textDecoration: st.underline ? 'underline' : 'none',
                          color: '#334155',
                          lineHeight: 1.5,
                        }}
                      >
                        {tmpl.consent_text || 'No statement configured.'}
                      </div>
                    </div>

                    {/* Typography & Style Chips */}
                    <div className="dummy-card-badges">
                      <span className="dummy-card-badge">
                        Font: {st.fontFamily ? st.fontFamily.replace(/['",]/g, '').split(' ')[0] : 'Inter'}
                      </span>
                      <span className="dummy-card-badge">
                        Size: {st.fontSize || '15px'}
                      </span>
                      <span className="dummy-card-badge">
                        Align: {st.textAlign || 'left'}
                      </span>
                      {st.bold && <span className="dummy-card-badge" style={{ background: '#dbeafe', color: '#1e40af' }}>Bold</span>}
                      {st.italic && <span className="dummy-card-badge" style={{ background: '#dbeafe', color: '#1e40af' }}>Italic</span>}
                      {st.underline && <span className="dummy-card-badge" style={{ background: '#dbeafe', color: '#1e40af' }}>Underline</span>}
                      {tmpl.updated_at && (
                        <span className="dummy-card-badge" style={{ marginLeft: 'auto', background: '#fafafa', color: '#94a3b8' }}>
                          {formatDate(tmpl.updated_at)}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Card Bottom Actions */}
                  <div className="dummy-card-actions">
                    <button
                      type="button"
                      className="dummy-btn dummy-btn-primary"
                      style={{ fontSize: '0.8rem', padding: '6px 12px' }}
                      onClick={() => loadTemplate(tmpl, true)}
                      title="Open and edit this template in builder studio"
                    >
                      <FontAwesomeIcon icon={faEdit} /> Edit in Builder
                    </button>

                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button
                        type="button"
                        className="dummy-btn dummy-btn-secondary"
                        style={{ fontSize: '0.78rem', padding: '6px 10px' }}
                        onClick={() => setPreviewModalTemplate(tmpl)}
                        title="Live interactive modal preview"
                      >
                        <FontAwesomeIcon icon={faEye} /> Preview
                      </button>

                      <button
                        type="button"
                        className="dummy-btn dummy-btn-secondary"
                        style={{ fontSize: '0.78rem', padding: '6px 10px' }}
                        onClick={() => handleDuplicate(tmpl)}
                        title="Duplicate template"
                      >
                        <FontAwesomeIcon icon={faClone} /> Copy
                      </button>

                      <button
                        type="button"
                        className="dummy-btn dummy-btn-danger"
                        style={{ fontSize: '0.78rem', padding: '6px 10px' }}
                        onClick={() => handleDelete(tmpl.id, tmpl.template_name)}
                        title="Delete template"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {filteredTemplates.length === 0 && (
            <div style={{
              background: '#ffffff',
              border: '1.5px dashed #cbd5e1',
              borderRadius: 14,
              padding: '48px 24px',
              textAlign: 'center',
              color: '#64748b'
            }}>
              <FontAwesomeIcon icon={faFileAlt} style={{ fontSize: '2.5rem', color: '#cbd5e1', marginBottom: 12 }} />
              <h3 style={{ margin: '0 0 6px 0', color: '#1e293b' }}>
                {searchQuery ? `No templates found matching "${searchQuery}"` : 'No Dummy Templates Available'}
              </h3>
              <p style={{ margin: '0 0 16px 0', fontSize: '0.88rem' }}>
                {searchQuery ? 'Try searching with different keywords.' : 'Create your first sample dummy consent template using the builder studio.'}
              </p>
              <button
                type="button"
                className="dummy-btn dummy-btn-primary"
                onClick={handleNewTemplate}
              >
                <FontAwesomeIcon icon={faPlus} /> Create New Template
              </button>
            </div>
          )}
        </div>
      ) : (
        /* ═════════════════════════════════════════════════════════════════════
           VIEW 2: INTERACTIVE BUILDER STUDIO (`viewMode === 'builder'`)
           ═════════════════════════════════════════════════════════════════════ */
        <div className="dummy-main-grid">
          {/* ── LEFT COLUMN: Configuration Controls ── */}
          <div className="dummy-editor-card">
            <div className="dummy-card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                <h2 className="dummy-card-title" style={{ margin: 0 }}>
                  <FontAwesomeIcon icon={faSlidersH} style={{ color: '#2563eb' }} /> {selectedTemplateId ? `Editing: ${templateName || 'Template #' + selectedTemplateId}` : 'New Template Draft'}
                </h2>
                <button
                  type="button"
                  onClick={() => setViewMode('cards')}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#2563eb',
                    fontWeight: 600,
                    fontSize: '0.82rem',
                    cursor: 'pointer',
                    textDecoration: 'underline'
                  }}
                >
                  ← Back to All Cards
                </button>
              </div>

              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className="dummy-btn dummy-btn-secondary"
                  onClick={handleReset}
                  title="Reset to last saved checkpoint"
                >
                  <FontAwesomeIcon icon={faUndo} /> Reset
                </button>

                {selectedTemplateId && (
                  <button
                    type="button"
                    className="dummy-btn dummy-btn-secondary"
                    onClick={() => handleSave(true)}
                    disabled={saving}
                    title="Duplicate / Save as a new template"
                  >
                    <FontAwesomeIcon icon={faClone} /> Save as New
                  </button>
                )}

                {selectedTemplateId && (
                  <button
                    type="button"
                    className="dummy-btn dummy-btn-danger"
                    onClick={() => handleDelete()}
                    title="Delete this dummy template"
                  >
                    <FontAwesomeIcon icon={faTrash} /> Delete
                  </button>
                )}

                <button
                  type="button"
                  className="dummy-btn dummy-btn-primary"
                  onClick={() => handleSave(false)}
                  disabled={saving}
                >
                  <FontAwesomeIcon icon={faSave} /> {saving ? 'Saving...' : 'Save Template'}
                </button>
              </div>
            </div>

            <div className="dummy-editor-body">
              {/* ── Section 1: Template Metadata ── */}
              <div className="dummy-section">
                <h3 className="dummy-section-title">
                  <span>1. Template Metadata</span>
                </h3>
                <div className="dummy-form-group">
                  <label className="dummy-label">Template Name *</label>
                  <input
                    type="text"
                    className="dummy-input"
                    placeholder="e.g., General Customer Privacy Consent"
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    required
                  />
                </div>
                <div className="dummy-form-group">
                  <label className="dummy-label">Internal Description</label>
                  <input
                    type="text"
                    className="dummy-input"
                    placeholder="Short note about where this template is used..."
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                </div>
              </div>

              {/* ── Section 2: Content & Rich Formatting ── */}
              <div className="dummy-section">
                <h3 className="dummy-section-title">
                  <span>2. Consent Title & Statement</span>
                  <FontAwesomeIcon icon={faFont} />
                </h3>

                <div className="dummy-form-group">
                  <label className="dummy-label">Consent Title</label>
                  <input
                    type="text"
                    className="dummy-input"
                    placeholder="e.g., Data Privacy & Consent Notice"
                    value={consentTitle}
                    onChange={(e) => setConsentTitle(e.target.value)}
                  />
                </div>

                {/* Formatting Toolbar */}
                <div className="dummy-toolbar">
                  <button
                    type="button"
                    className={`dummy-tool-btn ${styleConfig.bold ? 'active' : ''}`}
                    onClick={() => updateStyle('bold', !styleConfig.bold)}
                    title="Bold"
                  >
                    <strong>B</strong>
                  </button>
                  <button
                    type="button"
                    className={`dummy-tool-btn ${styleConfig.italic ? 'active' : ''}`}
                    onClick={() => updateStyle('italic', !styleConfig.italic)}
                    title="Italic"
                  >
                    <em>I</em>
                  </button>
                  <button
                    type="button"
                    className={`dummy-tool-btn ${styleConfig.underline ? 'active' : ''}`}
                    onClick={() => updateStyle('underline', !styleConfig.underline)}
                    title="Underline"
                  >
                    <u>U</u>
                  </button>

                  <div style={{ width: '1px', height: '18px', background: '#cbd5e1', margin: '0 4px' }} />

                  {/* Font Family Selector */}
                  <select
                    className="dummy-tool-select"
                    value={styleConfig.fontFamily}
                    onChange={(e) => updateStyle('fontFamily', e.target.value)}
                    title="Font Family"
                  >
                    <option value="'Inter', sans-serif">Inter (Modern Sans)</option>
                    <option value="Roboto, sans-serif">Roboto</option>
                    <option value="'Segoe UI', sans-serif">Segoe UI</option>
                    <option value="Georgia, serif">Georgia (Serif)</option>
                    <option value="Courier New, monospace">Courier (Monospace)</option>
                  </select>

                  {/* Font Size Selector */}
                  <select
                    className="dummy-tool-select"
                    value={styleConfig.fontSize}
                    onChange={(e) => updateStyle('fontSize', e.target.value)}
                    title="Font Size"
                  >
                    <option value="13px">Small (13px)</option>
                    <option value="15px">Medium (15px)</option>
                    <option value="17px">Large (17px)</option>
                    <option value="19px">Extra Large (19px)</option>
                  </select>

                  {/* Text Alignment */}
                  <select
                    className="dummy-tool-select"
                    value={styleConfig.textAlign}
                    onChange={(e) => updateStyle('textAlign', e.target.value)}
                    title="Text Alignment"
                  >
                    <option value="left">Left Align</option>
                    <option value="center">Center Align</option>
                    <option value="right">Right Align</option>
                    <option value="justify">Justify</option>
                  </select>
                </div>

                <div className="dummy-form-group">
                  <label className="dummy-label">Consent Statement / Body Text</label>
                  <textarea
                    className="dummy-textarea"
                    rows={6}
                    placeholder="Enter the complete consent statement or disclosure..."
                    value={consentText}
                    onChange={(e) => setConsentText(e.target.value)}
                  />
                </div>

                {/* Bottom Action Buttons */}
                <div style={{ marginTop: '1.25rem', paddingTop: '1rem', borderTop: '1px solid #f1f5f9', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    className="dummy-btn dummy-btn-primary"
                    style={{ flex: 1, padding: '11px', fontSize: '0.92rem', fontWeight: 700 }}
                    onClick={() => handleSave(false)}
                    disabled={saving}
                  >
                    <FontAwesomeIcon icon={faSave} /> {saving ? 'Saving to Database...' : 'Save Template'}
                  </button>
                  {selectedTemplateId && (
                    <button
                      type="button"
                      className="dummy-btn dummy-btn-secondary"
                      onClick={() => handleSave(true)}
                      disabled={saving}
                    >
                      <FontAwesomeIcon icon={faClone} /> Save as New
                    </button>
                  )}
                  <button
                    type="button"
                    className="dummy-btn dummy-btn-secondary"
                    onClick={handleReset}
                  >
                    <FontAwesomeIcon icon={faUndo} /> Reset
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* ── RIGHT COLUMN: Real-Time Interactive Live Preview ── */}
          <div className="dummy-preview-column">
            <div className="dummy-preview-card">
              <div className="dummy-preview-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <FontAwesomeIcon icon={faEye} style={{ color: '#38bdf8' }} />
                  <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>Live Preview</span>
                </div>

                {/* Viewport device switcher */}
                <div className="dummy-device-switcher">
                  <button
                    type="button"
                    className={`dummy-device-btn ${previewDevice === 'desktop' ? 'active' : ''}`}
                    onClick={() => setPreviewDevice('desktop')}
                    title="Desktop Preview"
                  >
                    <FontAwesomeIcon icon={faDesktop} /> Desktop
                  </button>
                  <button
                    type="button"
                    className={`dummy-device-btn ${previewDevice === 'tablet' ? 'active' : ''}`}
                    onClick={() => setPreviewDevice('tablet')}
                    title="Tablet Preview (600px)"
                  >
                    <FontAwesomeIcon icon={faTabletAlt} /> Tablet
                  </button>
                  <button
                    type="button"
                    className={`dummy-device-btn ${previewDevice === 'mobile' ? 'active' : ''}`}
                    onClick={() => setPreviewDevice('mobile')}
                    title="Mobile Preview (375px)"
                  >
                    <FontAwesomeIcon icon={faMobileAlt} /> Mobile
                  </button>
                </div>
              </div>

              {/* Preview Stage */}
              <div className="dummy-preview-stage">
                <div
                  className="dummy-rendered-dialog"
                  style={{
                    ...previewWidthStyle,
                    fontFamily: styleConfig.fontFamily || 'inherit',
                  }}
                >
                  {/* Visual Watermark Header */}
                  <div className="dummy-preview-watermark">
                    Dummy Template — Sample Only
                  </div>

                  <div style={{ padding: '1.75rem' }}>
                    {/* Title */}
                    <h3
                      style={{
                        margin: '0 0 1rem 0',
                        fontSize: '1.3rem',
                        fontWeight: 700,
                        color: '#0f172a',
                      }}
                    >
                      {consentTitle || 'Consent Title'}
                    </h3>

                    {/* Body Statement */}
                    <p
                      style={{
                        margin: 0,
                        fontSize: styleConfig.fontSize || '15px',
                        fontWeight: styleConfig.bold ? '700' : '400',
                        fontStyle: styleConfig.italic ? 'italic' : 'normal',
                        textDecoration: styleConfig.underline ? 'underline' : 'none',
                        textAlign: styleConfig.textAlign || 'left',
                        lineHeight: '1.65',
                        color: '#334155',
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {consentText || 'No consent statement provided.'}
                    </p>
                  </div>

                  {/* Footer simulation */}
                  <div
                    style={{
                      padding: '0.75rem 1.75rem',
                      borderTop: '1px solid #f1f5f9',
                      fontSize: '0.75rem',
                      color: '#94a3b8',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      backgroundColor: 'rgba(248, 250, 252, 0.7)',
                    }}
                  >
                    <span>Sample Consent Preview</span>
                    <span>Powered by Whitehats Technologies</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═════════════════════════════════════════════════════════════════════
         QUICK PREVIEW MODAL (`previewModalTemplate`)
         ═════════════════════════════════════════════════════════════════════ */}
      {previewModalTemplate && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(15,23,42,0.6)',
          backdropFilter: 'blur(3px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 99999,
          padding: 16
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: 14,
            maxWidth: 720,
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            padding: '24px',
            boxShadow: '0 20px 40px rgba(0,0,0,0.25)',
            border: '1.5px solid #e2e8f0'
          }}>
            {/* Modal Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, borderBottom: '1px solid #e2e8f0', paddingBottom: 12 }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '1.15rem', color: '#0f172a' }}>
                  Preview: {previewModalTemplate.template_name}
                </h3>
                <span style={{ fontSize: '0.78rem', color: '#64748b' }}>
                  Template ID: #{previewModalTemplate.id} • Sample Only
                </span>
              </div>

              {/* Modal Device Switcher */}
              <div className="dummy-device-switcher" style={{ background: '#0f172a' }}>
                <button
                  type="button"
                  className={`dummy-device-btn ${modalDevice === 'desktop' ? 'active' : ''}`}
                  onClick={() => setModalDevice('desktop')}
                >
                  <FontAwesomeIcon icon={faDesktop} /> Desktop
                </button>
                <button
                  type="button"
                  className={`dummy-device-btn ${modalDevice === 'tablet' ? 'active' : ''}`}
                  onClick={() => setModalDevice('tablet')}
                >
                  <FontAwesomeIcon icon={faTabletAlt} /> Tablet
                </button>
                <button
                  type="button"
                  className={`dummy-device-btn ${modalDevice === 'mobile' ? 'active' : ''}`}
                  onClick={() => setModalDevice('mobile')}
                >
                  <FontAwesomeIcon icon={faMobileAlt} /> Mobile
                </button>
              </div>

              <button
                type="button"
                onClick={() => setPreviewModalTemplate(null)}
                style={{ background: 'none', border: 'none', fontSize: '1.1rem', cursor: 'pointer', color: '#64748b' }}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            {/* Modal Rendered Preview */}
            <div style={{ background: '#f1f5f9', padding: '24px', borderRadius: 10, display: 'flex', justifyContent: 'center' }}>
              <div
                className="dummy-rendered-dialog"
                style={{
                  ...getDeviceWidthStyle(modalDevice),
                  fontFamily: previewModalTemplate.style_config?.fontFamily || "'Inter', sans-serif",
                }}
              >
                <div className="dummy-preview-watermark">
                  Dummy Template — Sample Only
                </div>
                <div style={{ padding: '1.75rem' }}>
                  <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.3rem', fontWeight: 700, color: '#0f172a' }}>
                    {previewModalTemplate.consent_title || 'Consent Notice'}
                  </h3>
                  <p style={{
                    margin: 0,
                    fontSize: previewModalTemplate.style_config?.fontSize || '15px',
                    fontWeight: previewModalTemplate.style_config?.bold ? '700' : '400',
                    fontStyle: previewModalTemplate.style_config?.italic ? 'italic' : 'normal',
                    textDecoration: previewModalTemplate.style_config?.underline ? 'underline' : 'none',
                    textAlign: previewModalTemplate.style_config?.textAlign || 'left',
                    lineHeight: '1.65',
                    color: '#334155',
                    whiteSpace: 'pre-wrap',
                  }}>
                    {previewModalTemplate.consent_text || 'No consent statement provided.'}
                  </p>
                </div>
                <div style={{
                  padding: '0.75rem 1.75rem',
                  borderTop: '1px solid #f1f5f9',
                  fontSize: '0.75rem',
                  color: '#94a3b8',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  backgroundColor: 'rgba(248, 250, 252, 0.7)',
                }}>
                  <span>Sample Consent Preview</span>
                  <span>Powered by Whitehats Technologies</span>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18, borderTop: '1px solid #e2e8f0', paddingTop: 14 }}>
              <button
                type="button"
                className="dummy-btn dummy-btn-primary"
                onClick={() => {
                  loadTemplate(previewModalTemplate, true);
                  setPreviewModalTemplate(null);
                }}
              >
                <FontAwesomeIcon icon={faEdit} /> Open in Builder Studio
              </button>
              <button
                type="button"
                className="dummy-btn dummy-btn-secondary"
                onClick={() => setPreviewModalTemplate(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
